import React, { useEffect, useState, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import { Link } from "react-router-dom";
import api from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Crosshair, MapPin, Phone, Plus, Globe, Search, Map as MapIcon, List } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { useAuth } from "../auth";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import AddressAutocomplete from "../components/AddressAutocomplete";

const POOL_TYPE_LABEL = {
  public_pool: "Piscine publique",
  // swimstars / petits_baigneurs / association are temporarily disabled (Iter35)
  // — kept in the map for back-compat with old data but never proposed for new pools.
  private: "Privée",
};

function makeIcon(type, isOsm) {
  const cls = isOsm ? "osm" : (type === "private" ? "private" : (type === "association" ? "assoc" : ""));
  return L.divIcon({
    className: "leaflet-div-icon",
    html: `<div class="aqua-marker ${cls}"><svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' fill='none' stroke='currentColor' stroke-width='2' viewBox='0 0 24 24'><path d='M2 6c2 0 2 2 4 2s2-2 4-2 2 2 4 2 2-2 4-2 2 2 4 2'/><path d='M2 12c2 0 2 2 4 2s2-2 4-2 2 2 4 2 2-2 4-2 2 2 4 2'/><path d='M2 18c2 0 2 2 4 2s2-2 4-2 2 2 4 2 2-2 4-2 2 2 4 2'/></svg></div>`,
    iconSize: [36, 36], iconAnchor: [18, 18],
  });
}

function Recenter({ center, zoom, trigger }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, zoom || map.getZoom(), { animate: true });
    // `trigger` is a counter incremented by the parent to force re-apply
    // (e.g. when the user changes the radius pill while staying on the same
    // search center — zoom value may be identical to the previous render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center?.[0], center?.[1], zoom, trigger]);
  return null;
}

function BoundsWatcher({ onChange }) {
  const map = useMapEvents({
    moveend: () => onChange(map.getBounds(), map.getZoom()),
    zoomend: () => onChange(map.getBounds(), map.getZoom()),
  });
  useEffect(() => { onChange(map.getBounds(), map.getZoom()); /* eslint-disable-next-line */ }, []);
  return null;
}

export default function MapView() {
  const { user } = useAuth();
  const [pools, setPools] = useState([]);
  const [osmPools, setOsmPools] = useState([]);
  const [allCount, setAllCount] = useState(0);
  const [center, setCenter] = useState([46.603354, 1.888334]);
  const [zoom, setZoom] = useState(6);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "public_pool", address: "", lat: "", lng: "", phone: "" });
  const [search, setSearch] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchCenter, setSearchCenter] = useState(null); // [lat, lng] of user's pinpointed search
  const [radiusKm, setRadiusKm] = useState(10);
  const [displayMode, setDisplayMode] = useState("map"); // "map" | "list"
  const [recenterTick, setRecenterTick] = useState(0);
  const osmTimeout = useRef(null);
  const searchTimeout = useRef(null);

  const RADIUS_ZOOMS = { 1: 15, 5: 13, 10: 12, 20: 11, 50: 10 };

  const haversineKm = (a, b) => {
    if (!a || !b) return Infinity;
    const R = 6371;
    const dLat = ((b[0] - a[0]) * Math.PI) / 180;
    const dLng = ((b[1] - a[1]) * Math.PI) / 180;
    const lat1 = (a[0] * Math.PI) / 180;
    const lat2 = (b[0] * Math.PI) / 180;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
  };

  const load = async (lat, lng) => {
    const params = lat && lng ? { lat, lng, radius_km: 200 } : {};
    const { data } = await api.get("/pools", { params });
    setPools(data);
  };
  useEffect(() => {
    load();
    api.get("/osm/pools/all").then((r) => setAllCount(r.data.count)).catch(() => {});
  }, []);

  const fetchOsm = (bounds, currentZoom) => {
    if (osmTimeout.current) clearTimeout(osmTimeout.current);
    if (currentZoom < 7) { setOsmPools([]); return; }
    osmTimeout.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/osm/pools", {
          params: {
            south: bounds.getSouth(), west: bounds.getWest(),
            north: bounds.getNorth(), east: bounds.getEast(),
            limit: 5000,
          },
        });
        setOsmPools(data.pools || []);
      } catch { setOsmPools([]); }
    }, 400);
  };

  // Fallback that builds an approximate bounding box around a point
  // and fetches OSM pools without depending on Leaflet's moveend event
  // (programmatic setView from Recenter doesn't always re-trigger BoundsWatcher).
  const fetchOsmAround = async (lat, lng, km = 8) => {
    const dLat = km / 111;
    const dLng = km / (111 * Math.cos((lat * Math.PI) / 180));
    try {
      const { data } = await api.get("/osm/pools", {
        params: {
          south: lat - dLat, west: lng - dLng,
          north: lat + dLat, east: lng + dLng,
          limit: 5000,
        },
      });
      setOsmPools(data.pools || []);
    } catch (e) { console.warn("OSM fetch échoué", e?.response?.status || e?.message); }
  };

  const locate = () => {
    if (!navigator.geolocation) return toast.error("Géolocalisation indisponible");
    navigator.geolocation.getCurrentPosition((pos) => {
      const c = [pos.coords.latitude, pos.coords.longitude];
      setCenter(c); setZoom(13);
      load(c[0], c[1]);
      fetchOsmAround(c[0], c[1], 8);
      toast.success("Position obtenue");
    }, () => toast.error("Impossible de vous localiser"));
  };

  const onSearchChange = (q) => {
    setSearch(q);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (q.length < 3) { setSearchSuggestions([]); return; }
    searchTimeout.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/geocode", { params: { q } });
        setSearchSuggestions(data.results || []);
      } catch { setSearchSuggestions([]); }
    }, 350);
  };

  const pickSearchResult = (r) => {
    const c = [r.lat, r.lng];
    setCenter(c);
    setZoom(RADIUS_ZOOMS[radiusKm] || 13);
    setSearchCenter(c);
    setSearch(r.label); setSearchSuggestions([]);
    load(r.lat, r.lng);
    fetchOsmAround(r.lat, r.lng, Math.max(radiusKm, 5));
  };

  const clearSearch = () => {
    setSearch("");
    setSearchCenter(null);
    setSearchSuggestions([]);
  };

  const filtered = useMemo(() => {
    let list = pools;
    if (filter !== "all") list = list.filter((p) => p.type === filter);
    if (searchCenter) list = list.filter((p) => haversineKm(searchCenter, [p.lat, p.lng]) <= radiusKm);
    return list;
  }, [pools, filter, searchCenter, radiusKm]);

  const dedupOsm = useMemo(() => {
    const tooClose = (a, b) => {
      const dx = (a.lat - b.lat) * 111000;
      const dy = (a.lng - b.lng) * 111000 * Math.cos((a.lat * Math.PI) / 180);
      return Math.sqrt(dx * dx + dy * dy) < 150;
    };
    let list = osmPools.filter((o) => !pools.some((p) => tooClose(p, o)));
    if (searchCenter) list = list.filter((o) => haversineKm(searchCenter, [o.lat, o.lng]) <= radiusKm);
    return list;
  }, [osmPools, pools, searchCenter, radiusKm]);

  const submitPool = async (e) => {
    e.preventDefault();
    if (!form.lat || !form.lng) {
      return toast.error("Coordonnées manquantes — utilisez 'Géolocaliser cette adresse' ou cliquez sur la carte.");
    }
    try {
      const payload = { ...form, lat: parseFloat(form.lat), lng: parseFloat(form.lng) };
      await api.post("/pools", payload);
      toast.success("Établissement ajouté");
      setOpen(false);
      setForm({ name: "", type: "public_pool", address: "", lat: "", lng: "", phone: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const geocodeAddress = async () => {
    if (!form.address) return toast.error("Saisissez d'abord une adresse");
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(form.address)}`);
      const data = await r.json();
      if (!data.length) return toast.error("Adresse introuvable. Essayez une formulation plus précise.");
      const { lat, lon, display_name } = data[0];
      setForm((f) => ({ ...f, lat: String(lat), lng: String(lon), address: f.address || display_name }));
      toast.success("Coordonnées trouvées ✓");
    } catch {
      toast.error("Erreur géocodage");
    }
  };

  const claimOsm = (osm) => {
    setForm({ name: osm.name, type: "public_pool", address: osm.address || "",
              lat: String(osm.lat), lng: String(osm.lng), phone: osm.phone || "" });
    setOpen(true);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Toutes les piscines de France</h1>
          <p className="text-slate-600 text-sm">
            {allCount > 0 ? `${allCount.toLocaleString("fr-FR")} piscines référencées (OpenStreetMap).` : "Carte alimentée par OpenStreetMap."} Cliquez sur une piscine pour voir les MNS rattachés.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {/* Pool-type filter temporarily hidden per user request (May 2026) — kept in code for later re-enable */}
          {false && (
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[200px] rounded-full" data-testid="filter-pools"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes (référencées)</SelectItem>
              <SelectItem value="public_pool">Piscines publiques</SelectItem>
              <SelectItem value="swimstars">Swimstars</SelectItem>
              <SelectItem value="petits_baigneurs">Les Petits Baigneurs</SelectItem>
              <SelectItem value="association">Associations</SelectItem>
              <SelectItem value="private">Privées</SelectItem>
            </SelectContent>
          </Select>
          )}
          <Button onClick={locate} className="rounded-full bg-sky-700 hover:bg-sky-600" data-testid="locate-btn"><Crosshair className="w-4 h-4 mr-1" />Voir les piscines autour de moi</Button>
          {user && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="add-pool-btn"><Plus className="w-4 h-4 mr-1" />Ajouter un établissement</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Ajouter un établissement</DialogTitle></DialogHeader>
                <form onSubmit={submitPool} className="space-y-3">
                  <div><Label>Nom</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div>
                    <Label>Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(POOL_TYPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Adresse</Label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <AddressAutocomplete
                          value={form.address}
                          onChange={(v) => setForm({ ...form, address: v })}
                          onSelect={(it) => {
                            setForm((f) => ({
                              ...f,
                              address: it.display_name || it.street || f.address,
                              lat: it.lat ? String(it.lat) : f.lat,
                              lng: it.lon ? String(it.lon) : f.lng,
                            }));
                          }}
                          placeholder="ex: 27 Bd du Général Koenig, 92200 Neuilly-sur-Seine"
                          testId="add-pool-address"
                          required
                        />
                      </div>
                      <Button type="button" variant="outline" className="rounded-full whitespace-nowrap" onClick={geocodeAddress} data-testid="geocode-pool-btn"><MapPin className="w-3 h-3 mr-1" />Géolocaliser</Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Latitude</Label><Input required type="number" step="any" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} data-testid="add-pool-lat" /></div>
                    <div><Label>Longitude</Label><Input required type="number" step="any" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} data-testid="add-pool-lng" /></div>
                  </div>
                  <p className="text-[11px] text-slate-500">💡 Astuce : saisissez l'adresse puis cliquez "Géolocaliser" pour remplir automatiquement les coordonnées.</p>
                  <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                  <DialogFooter className="flex-col sm:flex-row gap-2">
                    <Button type="button" variant="outline" className="rounded-full" onClick={() => setOpen(false)} data-testid="cancel-pool-btn">Annuler</Button>
                    <Button type="submit" className="rounded-full bg-sky-700 hover:bg-sky-600" data-testid="submit-pool-btn">Ajouter l'établissement</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <p className="text-[11px] text-slate-500 mb-2">En cliquant sur "Voir les piscines autour de moi", vous acceptez d'activer votre localisation pour afficher les piscines proches.</p>
      <div className="relative mb-3 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center" data-testid="search-bar">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Rechercher par adresse ou code postal (ex: 75008 ou 'Avenue des Champs-Élysées')"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 rounded-full"
            data-testid="search-input"
          />
          {searchSuggestions.length > 0 && (
            <div className="absolute mt-1 w-full bg-white border rounded-xl shadow-lg overflow-hidden" style={{ zIndex: 1500 }}>
              {searchSuggestions.map((r, i) => (
                <button key={`${r.lat}-${r.lng}-${r.label}`} onClick={() => pickSearchResult(r)} data-testid={`search-result-${i}`}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-sky-50 border-b last:border-0">
                  <MapPin className="w-3 h-3 inline mr-1 text-sky-700" />{r.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-1 items-center bg-slate-100 rounded-full p-1" data-testid="radius-pills">
          <span className="text-xs text-slate-500 px-2 hidden sm:inline">Rayon :</span>
          {[1, 5, 10, 20, 50].map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => {
                setRadiusKm(r);
                const newZoom = RADIUS_ZOOMS[r] || 13;
                setZoom(newZoom);
                // Always re-apply the view on the current search center so the
                // map visibly zooms on mobile too (parent state may not change
                // if the user picks the same zoom level consecutively).
                if (searchCenter) setCenter([searchCenter[0], searchCenter[1]]);
                setRecenterTick((v) => v + 1);
              }}
              className={`text-xs sm:text-sm px-3 py-1 rounded-full transition ${radiusKm === r ? "bg-sky-700 text-white shadow" : "text-slate-700 hover:bg-white"}`}
              data-testid={`radius-pill-${r}`}
            >
              {r} km
            </button>
          ))}
        </div>
        {searchCenter && (
          <Button variant="outline" size="sm" className="rounded-full" onClick={clearSearch} data-testid="clear-search-btn">Effacer</Button>
        )}
        <div className="inline-flex rounded-full border bg-white overflow-hidden shadow-sm" role="tablist" data-testid="view-toggle">
          <button
            onClick={() => setDisplayMode("map")}
            className={`px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1 transition ${displayMode === "map" ? "bg-sky-700 text-white" : "text-slate-600 hover:bg-sky-50"}`}
            data-testid="view-map-btn"
          >
            <MapIcon className="w-3.5 h-3.5" />Carte
          </button>
          <button
            onClick={() => setDisplayMode("list")}
            className={`px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1 transition ${displayMode === "list" ? "bg-sky-700 text-white" : "text-slate-600 hover:bg-sky-50"}`}
            data-testid="view-list-btn"
          >
            <List className="w-3.5 h-3.5" />Liste
          </button>
        </div>
      </div>

      {displayMode === "list" ? (
        <PoolsList
          pools={filtered}
          osmPools={dedupOsm}
          searchCenter={searchCenter}
          user={user}
          onClaimOsm={claimOsm}
        />
      ) : (
        <div className="card-soft overflow-hidden h-[55vh] min-h-[320px] sm:h-[640px] relative">
        <MapContainer center={center} zoom={zoom} style={{ height: "100%", width: "100%" }}>
          <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <Recenter center={center} zoom={zoom} trigger={recenterTick} />
          <BoundsWatcher onChange={(b, z) => fetchOsm(b, z)} />
          <MarkerClusterGroup chunkedLoading maxClusterRadius={50}>
            {filtered.map((p) => (
              <Marker key={p.pool_id} position={[p.lat, p.lng]} icon={makeIcon(p.type, false)}>
                <Popup>
                  <div className="space-y-1 min-w-[200px]">
                    <div className="font-semibold text-slate-900">{p.name}</div>
                    <div className="text-xs text-slate-500">{POOL_TYPE_LABEL[p.type]}</div>
                    <div className="text-sm text-slate-700">{p.address}</div>
                    {p.phone && <div className="text-sm flex items-center gap-1 text-slate-600"><Phone className="w-3 h-3" />{p.phone}</div>}
                    <Link to={`/pool/${p.pool_id}`} className="inline-block mt-2 text-sky-700 font-medium text-sm">Voir les MNS →</Link>
                  </div>
                </Popup>
              </Marker>
            ))}
            {dedupOsm.map((o) => (
              <Marker key={o.osm_id} position={[o.lat, o.lng]} icon={makeIcon(null, true)}>
                <Popup>
                  <div className="space-y-1 min-w-[200px]">
                    <div className="font-semibold text-slate-900">{o.name}</div>
                    <div className="text-xs text-emerald-700 flex items-center gap-1"><Globe className="w-3 h-3" />Source : OpenStreetMap</div>
                    {o.address && <div className="text-sm text-slate-700">{o.address}</div>}
                    {o.phone && <div className="text-sm flex items-center gap-1 text-slate-600"><Phone className="w-3 h-3" />{o.phone}</div>}
                    {o.fee && <div className="text-xs text-slate-500">Accès payant : {o.fee}</div>}
                    {o.website && <a href={o.website} target="_blank" rel="noreferrer" className="text-xs text-sky-700">Site web</a>}
                    {user && (
                      <button onClick={() => claimOsm(o)} className="block mt-2 text-sky-700 font-medium text-sm" data-testid={`claim-${o.osm_id}`}>+ Référencer cet établissement</button>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MarkerClusterGroup>
        </MapContainer>
        <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur rounded-full px-3 py-1 text-xs text-slate-600 shadow flex items-center gap-2">
          <span><span className="inline-block w-2 h-2 rounded-full bg-sky-700 mr-1" />{filtered.length} référencée(s)</span>
          <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-600 mr-1" />{dedupOsm.length} OSM dans la vue</span>
          {searchCenter && <span className="text-sky-700">• Rayon {radiusKm} km</span>}
        </div>
        </div>
      )}
    </div>
  );
}

function PoolsList({ pools, osmPools, searchCenter, user, onClaimOsm }) {
  const haversineKm = (a, b) => {
    if (!a || !b) return null;
    const R = 6371;
    const dLat = ((b[0] - a[0]) * Math.PI) / 180;
    const dLng = ((b[1] - a[1]) * Math.PI) / 180;
    const lat1 = (a[0] * Math.PI) / 180;
    const lat2 = (b[0] * Math.PI) / 180;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
  };
  const all = [
    ...pools.map((p) => ({ ...p, _kind: "ref" })),
    ...osmPools.map((o) => ({ ...o, _kind: "osm" })),
  ];
  const withDist = all
    .map((p) => ({ ...p, _dist: searchCenter ? haversineKm(searchCenter, [p.lat, p.lng]) : null }))
    .sort((a, b) => {
      if (a._dist != null && b._dist != null) return a._dist - b._dist;
      if (a._dist != null) return -1;
      if (b._dist != null) return 1;
      return (a.name || "").localeCompare(b.name || "");
    });

  if (withDist.length === 0) {
    return (
      <div className="card-soft p-10 text-center text-slate-500" data-testid="pools-list-empty">
        Aucune piscine dans cette zone. Essayez d'élargir le rayon ou de rechercher une autre adresse.
      </div>
    );
  }

  return (
    <div className="card-soft overflow-hidden" data-testid="pools-list">
      <div className="flex items-center justify-between px-4 py-2 border-b bg-gradient-to-r from-sky-50 via-white to-cyan-50 text-xs text-slate-600">
        <span><strong>{withDist.length}</strong> piscine{withDist.length > 1 ? "s" : ""} {searchCenter ? "dans la zone" : "affichée"}{withDist.length > 1 ? "s" : ""}</span>
        <span className="text-[11px] text-slate-500">Triées par {searchCenter ? "distance" : "nom"}</span>
      </div>
      <div className="divide-y max-h-[70vh] overflow-y-auto">
        {withDist.map((p) => (
          <div key={p._kind === "ref" ? p.pool_id : p.osm_id} className="p-4 flex items-center gap-3 hover:bg-sky-50/60 transition" data-testid={`pool-list-${p._kind === "ref" ? p.pool_id : p.osm_id}`}>
            <div className={`shrink-0 w-11 h-11 rounded-full flex items-center justify-center ${p._kind === "osm" ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"}`}>
              <MapPin className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-900 truncate">{p.name}</span>
                {p._kind === "osm" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center gap-1"><Globe className="w-2.5 h-2.5" />OSM</span>}
                {p._kind === "ref" && p.type && <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">{POOL_TYPE_LABEL[p.type] || p.type}</span>}
              </div>
              {p.address && <div className="text-xs text-slate-500 truncate mt-0.5">{p.address}</div>}
              <div className="flex items-center gap-3 mt-1 text-xs text-slate-600">
                {p._dist != null && (
                  <span className="inline-flex items-center gap-1 text-sky-700 font-semibold">
                    <Crosshair className="w-3 h-3" />{p._dist < 1 ? `${Math.round(p._dist * 1000)} m` : `${p._dist.toFixed(1)} km`}
                  </span>
                )}
                {p.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{p.phone}</span>}
              </div>
            </div>
            {p._kind === "ref" ? (
              <Link
                to={`/pool/${p.pool_id}`}
                className="shrink-0 text-xs px-3 py-1.5 rounded-full bg-sky-700 text-white hover:bg-sky-600 font-medium"
                data-testid={`pool-list-open-${p.pool_id}`}
              >
                Voir les MNS →
              </Link>
            ) : user ? (
              <button
                onClick={() => onClaimOsm(p)}
                className="shrink-0 text-xs px-3 py-1.5 rounded-full border border-sky-300 text-sky-700 hover:bg-sky-50 font-medium"
                data-testid={`pool-list-claim-${p.osm_id}`}
              >
                + Référencer
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
